import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, BellRing, BellOff, CheckCheck, RefreshCw, MailCheck, Target, Repeat } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { isPushSupported, getPushStatus, subscribePush, unsubscribePush } from "@/lib/push";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({ email_enabled: true, budget_alerts: true, recurring_reminders: true });
  const [scanning, setScanning] = useState(false);
  const [pushStatus, setPushStatus] = useState({ supported: false, permission: "default", subscribed: false });

  const load = async () => {
    const [{ data: n }, { data: s }] = await Promise.all([
      api.get("/notifications"),
      api.get("/notifications/settings"),
    ]);
    setItems(n);
    setSettings(s);
  };

  useEffect(() => { load(); getPushStatus().then(setPushStatus); }, []);

  const togglePush = async () => {
    try {
      if (pushStatus.subscribed) {
        await unsubscribePush();
        toast.success("Push notifications disabled");
      } else {
        if (!(await isPushSupported())) {
          toast.error("Push notifications not supported in this browser");
          return;
        }
        await subscribePush();
        toast.success("Push notifications enabled");
      }
      setPushStatus(await getPushStatus());
    } catch (e) {
      toast.error(e.message || "Could not update push");
    }
  };

  const check = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/notifications/check");
      toast.success(`Checked · ${data.created} new`);
      await load();
      window.dispatchEvent(new Event("ledger:notifications-updated"));
    } finally {
      setScanning(false);
    }
  };

  const readAll = async () => {
    await api.post("/notifications/read-all");
    await load();
    window.dispatchEvent(new Event("ledger:notifications-updated"));
  };

  const readOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    window.dispatchEvent(new Event("ledger:notifications-updated"));
  };

  const saveSetting = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    await api.patch("/notifications/settings", next);
    toast.success("Preferences saved");
  };

  const unread = items.filter((n) => !n.read).length;

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Alerts</div>
          <h1 className="font-display text-4xl mt-2">Notifications</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">
            Budget breaches and upcoming recurring — in the app and by email.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="notif-scan-btn"
            variant="outline"
            onClick={check}
            disabled={scanning}
            className="rounded-full h-11 px-4"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${scanning ? "animate-spin" : ""}`} />
            Scan now
          </Button>
          {unread > 0 && (
            <Button
              data-testid="notif-read-all"
              onClick={readAll}
              className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8] h-11 px-4"
            >
              <CheckCheck className="w-4 h-4 mr-2" /> Mark all read
            </Button>
          )}
        </div>
      </div>

      <div className="card-flat p-6 mb-6">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Preferences</div>
        <h3 className="font-display text-xl mt-1 mb-4">What should we alert you about?</h3>
        <div data-testid="notif-settings" className="space-y-3">
          <div className="flex items-center justify-between p-3 rounded-md border border-[var(--border)]">
            <div className="flex items-center gap-3">
              {pushStatus.subscribed ? <BellRing className="w-4 h-4 text-[#4C956C]" /> : <BellOff className="w-4 h-4 text-[var(--muted-text)]" />}
              <div>
                <Label className="text-sm">Web push notifications</Label>
                <div className="text-xs text-[var(--muted-text)]">
                  {pushStatus.supported
                    ? (pushStatus.subscribed ? "You'll receive real-time push notifications on this device." : "Get instant push notifications — even when Accountrix is closed.")
                    : "This browser doesn't support push notifications."}
                </div>
              </div>
            </div>
            <Switch
              data-testid="pref-push"
              checked={pushStatus.subscribed}
              disabled={!pushStatus.supported}
              onCheckedChange={togglePush}
            />
          </div>
          <div className="flex items-center justify-between p-3 rounded-md border border-[var(--border)]">
            <div className="flex items-center gap-3">
              <MailCheck className="w-4 h-4 text-[var(--muted-text)]" />
              <div>
                <Label className="text-sm">Email alerts</Label>
                <div className="text-xs text-[var(--muted-text)]">Get a copy of important alerts by email.</div>
              </div>
            </div>
            <Switch
              data-testid="pref-email"
              checked={settings.email_enabled}
              onCheckedChange={(v) => saveSetting({ email_enabled: v })}
            />
          </div>
          <div className="flex items-center justify-between p-3 rounded-md border border-[var(--border)]">
            <div className="flex items-center gap-3">
              <Target className="w-4 h-4 text-[var(--muted-text)]" />
              <div>
                <Label className="text-sm">Budget alerts</Label>
                <div className="text-xs text-[var(--muted-text)]">Notify at 80% and 100% of any category budget.</div>
              </div>
            </div>
            <Switch
              data-testid="pref-budget"
              checked={settings.budget_alerts}
              onCheckedChange={(v) => saveSetting({ budget_alerts: v })}
            />
          </div>
          <div className="flex items-center justify-between p-3 rounded-md border border-[var(--border)]">
            <div className="flex items-center gap-3">
              <Repeat className="w-4 h-4 text-[var(--muted-text)]" />
              <div>
                <Label className="text-sm">Recurring reminders</Label>
                <div className="text-xs text-[var(--muted-text)]">3-day heads-up before a recurring transaction runs.</div>
              </div>
            </div>
            <Switch
              data-testid="pref-recurring"
              checked={settings.recurring_reminders}
              onCheckedChange={(v) => saveSetting({ recurring_reminders: v })}
            />
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div data-testid="notif-empty" className="card-flat p-12 text-center">
          <Bell className="w-8 h-8 mx-auto text-[var(--muted-text)]" />
          <h3 className="font-display text-xl mt-3">Nothing to report</h3>
          <p className="text-sm text-[var(--muted-text)] mt-1">You&apos;re on top of things. Hit &quot;Scan now&quot; if you added something.</p>
        </div>
      ) : (
        <div data-testid="notif-list" className="card-flat divide-y divide-[var(--divider)]">
          {items.map((n) => (
            <div
              key={n.id}
              data-testid={`notif-row-${n.id}`}
              className={`p-4 flex items-start gap-3 ${!n.read ? "bg-[var(--row-hover)]" : ""}`}
            >
              <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${
                n.kind === "budget" ? "bg-[#FCE7E9] text-[#E63946]" : "bg-[#F5EAE3] text-[#E07A5F]"
              }`}>
                {n.kind === "budget" ? <Target className="w-4 h-4" /> : <Repeat className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{n.title}</div>
                <div className="text-xs text-[var(--muted-text)] mt-0.5">{n.body}</div>
                <div className="text-[11px] text-[var(--muted-text)] mt-1">
                  {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                </div>
              </div>
              {!n.read && (
                <Button
                  data-testid={`notif-read-${n.id}`}
                  size="sm" variant="ghost"
                  onClick={() => readOne(n.id)}
                >
                  Mark read
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
